import { Head, Link, router, usePage } from "@inertiajs/react"
import { ArrowLeft, Link2, Rocket, Save } from "lucide-react"
import { useEffect, useMemo, useState } from "react"

import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import AppLayout from "@/layouts/app-layout"

interface CampaignForm {
  id?: number
  title: string
  subtitle?: string
  slug: string
  status: string
  prize_value?: number
  prize_image_url?: string
  base_entries: number
  entries_per_referral: number
  max_entries_per_user?: number | null
  max_referral_levels: number
  start_date?: string
  end_date?: string
  draw_date?: string | null
  primary_color?: string
  background_image_url?: string
  logo_url?: string
  sponsor_id?: number | null
  host_type?: string | null
  host_id?: number | null
}

interface HostsProps {
  raffles: { id: number; title: string }[]
}

interface PageProps {
  campaign: CampaignForm
  hosts: HostsProps
  statuses: string[]
  errors?: string[]
}

export default function ViralCampaignsForm() {
  const page = usePage()
  const {
    campaign,
    hosts,
    statuses,
    errors = [],
  } = page.props as unknown as PageProps

  const [form, setForm] = useState<CampaignForm>({
    id: campaign.id,
    title: campaign.title ?? "",
    subtitle: campaign.subtitle ?? "",
    slug: campaign.slug ?? "",
    status: campaign.status ?? "draft",
    prize_value: campaign.prize_value ?? undefined,
    prize_image_url: campaign.prize_image_url ?? undefined,
    base_entries: campaign.base_entries ?? 1,
    entries_per_referral: campaign.entries_per_referral ?? 5,
    max_entries_per_user: campaign.max_entries_per_user ?? null,
    max_referral_levels: campaign.max_referral_levels ?? 1,
    start_date: campaign.start_date ?? undefined,
    end_date: campaign.end_date ?? undefined,
    draw_date: campaign.draw_date ?? null,
    primary_color: campaign.primary_color ?? "#00B4D8",
    background_image_url: campaign.background_image_url ?? undefined,
    logo_url: campaign.logo_url ?? undefined,
    sponsor_id: campaign.sponsor_id ?? null,
    host_type: campaign.host_type ?? null,
    host_id: campaign.host_id ?? null,
  })

  const isEdit = useMemo(() => Boolean(form.id), [form.id])

  const submit = (e: React.FormEvent) => {
    e.preventDefault()

    // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-assignment
    const payload = { campaign: form } as any
    if (isEdit) {
      void router.put(`/viral/campaigns/${form.id}`, payload)
    } else {
      void router.post("/viral/campaigns", payload)
    }
  }

  useEffect(() => {
    if (!form.slug && form.title) {
      setForm((f) => ({
        ...f,
        slug: f.title
          .toLowerCase()
          .replace(/\s+/g, "-")
          .replace(/[^a-z0-9-]/g, ""),
      }))
    }
  }, [form.title, form.slug])

  return (
    <AppLayout title={isEdit ? "Editar campaña viral" : "Nueva campaña viral"}>
      <Head title={isEdit ? "Editar campaña viral" : "Nueva campaña viral"} />

      <form
        onSubmit={submit}
        className="mx-auto max-w-4xl space-y-6 p-4 md:p-6"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Rocket className="text-primary h-6 w-6" />
            <h1 className="text-2xl font-bold">
              {isEdit ? "Editar campaña viral" : "Nueva campaña viral"}
            </h1>
          </div>
          <div className="flex gap-2">
            <Link href="/viral/campaigns">
              <Button type="button" variant="outline">
                <ArrowLeft className="mr-2 h-4 w-4" /> Volver
              </Button>
            </Link>
            <Button type="submit">
              <Save className="mr-2 h-4 w-4" /> Guardar
            </Button>
          </div>
        </div>

        {errors.length > 0 && (
          <div className="border-destructive/30 bg-destructive/5 text-destructive rounded border p-3 text-sm">
            {errors.map((e, idx) => (
              <div key={idx}>• {e}</div>
            ))}
          </div>
        )}

        <Card>
          <CardHeader>
            <CardTitle>Información básica</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <Label htmlFor="title">Título</Label>
              <Input
                id="title"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="slug">Slug</Label>
              <Input
                id="slug"
                value={form.slug}
                onChange={(e) => setForm({ ...form, slug: e.target.value })}
              />
            </div>
            <div className="md:col-span-2">
              <Label htmlFor="subtitle">Subtítulo</Label>
              <Input
                id="subtitle"
                value={form.subtitle ?? ""}
                onChange={(e) => setForm({ ...form, subtitle: e.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="status">Estado</Label>
              <Select
                value={form.status}
                onValueChange={(v) => setForm({ ...form, status: v })}
              >
                <SelectTrigger id="status">
                  <SelectValue placeholder="Selecciona estado" />
                </SelectTrigger>
                <SelectContent>
                  {statuses.map((s) => (
                    <SelectItem key={s} value={s}>
                      {s}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="prize_value">Valor del premio</Label>
              <Input
                id="prize_value"
                type="number"
                step="0.01"
                value={form.prize_value ?? ""}
                onChange={(e) =>
                  setForm({ ...form, prize_value: Number(e.target.value) })
                }
              />
            </div>
            <div className="md:col-span-2">
              <Label htmlFor="prize_image_url">Imagen del premio (URL)</Label>
              <Input
                id="prize_image_url"
                value={form.prize_image_url ?? ""}
                onChange={(e) =>
                  setForm({ ...form, prize_image_url: e.target.value })
                }
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Mecánica</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <Label htmlFor="base_entries">Entradas base</Label>
              <Input
                id="base_entries"
                type="number"
                value={form.base_entries}
                onChange={(e) =>
                  setForm({ ...form, base_entries: Number(e.target.value) })
                }
              />
            </div>
            <div>
              <Label htmlFor="entries_per_referral">
                Entradas por referido
              </Label>
              <Input
                id="entries_per_referral"
                type="number"
                value={form.entries_per_referral}
                onChange={(e) =>
                  setForm({
                    ...form,
                    entries_per_referral: Number(e.target.value),
                  })
                }
              />
            </div>
            <div>
              <Label htmlFor="max_entries_per_user">
                Máx. entradas por usuario
              </Label>
              <Input
                id="max_entries_per_user"
                type="number"
                value={form.max_entries_per_user ?? ""}
                onChange={(e) =>
                  setForm({
                    ...form,
                    max_entries_per_user:
                      e.target.value === "" ? null : Number(e.target.value),
                  })
                }
              />
            </div>
            <div>
              <Label htmlFor="max_referral_levels">Niveles de referidos</Label>
              <Input
                id="max_referral_levels"
                type="number"
                value={form.max_referral_levels}
                onChange={(e) =>
                  setForm({
                    ...form,
                    max_referral_levels: Number(e.target.value),
                  })
                }
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Fechas</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <div>
              <Label htmlFor="start_date">Inicio</Label>
              <Input
                id="start_date"
                type="datetime-local"
                value={form.start_date ?? ""}
                onChange={(e) =>
                  setForm({ ...form, start_date: e.target.value })
                }
              />
            </div>
            <div>
              <Label htmlFor="end_date">Fin</Label>
              <Input
                id="end_date"
                type="datetime-local"
                value={form.end_date ?? ""}
                onChange={(e) => setForm({ ...form, end_date: e.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="draw_date">Sorteo</Label>
              <Input
                id="draw_date"
                type="datetime-local"
                value={form.draw_date ?? ""}
                onChange={(e) =>
                  setForm({
                    ...form,
                    draw_date: e.target.value === "" ? null : e.target.value,
                  })
                }
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Asociación (opcional)</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <Label htmlFor="host_type">Tipo de host</Label>
              <Select
                value={form.host_type ?? "none"}
                onValueChange={(v) =>
                  setForm({
                    ...form,
                    host_type: v === "none" ? null : v,
                    host_id: null,
                  })
                }
              >
                <SelectTrigger id="host_type">
                  <SelectValue placeholder="Sin asociación" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Sin asociación</SelectItem>
                  <SelectItem value="Raffle">Raffle</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="host_id">Recurso</Label>
              {form.host_type === "Raffle" ? (
                <Select
                  value={(form.host_id ?? "none").toString()}
                  onValueChange={(v) =>
                    setForm({
                      ...form,
                      host_id: v === "none" ? null : Number(v),
                    })
                  }
                >
                  <SelectTrigger id="host_id">
                    <SelectValue placeholder="Selecciona sorteo" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Ninguno</SelectItem>
                    {hosts.raffles.map((r) => (
                      <SelectItem key={r.id} value={String(r.id)}>
                        {r.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <div className="text-muted-foreground flex items-center gap-2 text-sm">
                  <Link2 className="h-4 w-4" /> No hay recurso seleccionado
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </form>
    </AppLayout>
  )
}
